import { useState } from "react";
import axios from "axios";
import { Mail, Phone, Instagram, Facebook, Youtube, Twitter, Music2, LinkIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import { API } from "../lib/api";


const social = [
  { href: "https://instagram.com/Shaycooksllc", icon: Instagram, label: "Instagram" },
  { href: "https://tiktok.com/@Shaycooksllc", icon: Music2, label: "TikTok" },
  { href: "https://facebook.com/Shaycooksllc", icon: Facebook, label: "Facebook" },
  { href: "https://youtube.com/@Shaycooksllc", icon: Youtube, label: "YouTube" },
  { href: "https://x.com/Shaycooksllc", icon: Twitter, label: "X" },
  { href: "https://linktr.ee/Shaycooksllc", icon: LinkIcon, label: "Linktree" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent", { description: "Shay will get back to you within 24 hours." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send message", { description: "Please try again or email us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-shay-cream min-h-screen pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="text-center mb-16">
          <p className="eyebrow">Get In Touch</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-shay-navy mt-4 tracking-tight leading-[0.95]">
            Say <span className="italic text-shay-gold">hello.</span>
          </h1>
          <p className="mt-5 max-w-lg mx-auto text-shay-chocolate/75">
            Custom orders, events, or just want to say what's up? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-shay-gold/30 p-7 md:p-10" data-testid="contact-form">
              <h2 className="font-display text-2xl md:text-3xl text-shay-navy">Send a message</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-4">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="field-input" data-testid="contact-name" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="field-input" data-testid="contact-phone" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="field-input sm:col-span-2" data-testid="contact-email" />
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What's on your mind?" className="field-input sm:col-span-2 resize-none" data-testid="contact-message" />
              </div>
              <button type="submit" disabled={submitting} className="btn-gold mt-7 disabled:opacity-60" data-testid="contact-submit-btn">
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>

          {/* Sidebar */}
          <Reveal delay={0.1} className="lg:col-span-5 space-y-8">
            <div className="bg-shay-navy text-shay-cream p-8">
              <p className="eyebrow eyebrow-gold">Direct Lines</p>
              <ul className="mt-5 space-y-5">
                <li className="flex items-start gap-4">
                  <Mail size={18} className="text-shay-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-shay-cream/60">Email</p>
                    <a href="mailto:Business@ShayCooks.com" className="font-display text-xl hover:text-shay-gold" data-testid="contact-email-link">
                      Business@ShayCooks.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={18} className="text-shay-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-shay-cream/60">Phone</p>
                    <a href="tel:+14077340119" className="font-display text-xl hover:text-shay-gold" data-testid="contact-phone-link">
                      (407) 734-0119
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={18} className="text-shay-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-shay-cream/60">Service Area</p>
                    <p className="font-display text-xl">Orlando, FL & nearby</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-shay-gold/30 p-8">
              <p className="eyebrow">Find Us Online</p>
              <p className="font-display text-2xl text-shay-navy mt-2">@ShayCooksllc</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {social.map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`contact-social-${label.toLowerCase()}`}
                    aria-label={label}
                    className="h-11 w-11 grid place-items-center rounded-full border border-shay-navy/15 text-shay-navy hover:bg-shay-navy hover:text-shay-gold hover:border-shay-navy transition-colors"
                  >
                    <Icon size={17} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
